import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CellValueChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { formatDate } from 'date-fns';
import { cloneDeep } from 'lodash';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { fertilizationAppApi } from '@/app/services/fertilizationApp';
import {
  UpdateApprovalNumberInput,
  UpdateODFreezeInput,
} from '@/rtk-query.gql';
import { ToastLife, useToast } from '@/services/toast';

import {
  useApplyApprovalNumbersToEmbryos,
  useFertAndEmbryosFreezeQuery,
  useFertilizationListFreezeQuery,
  useInvalidateFreezeQueries,
  useUpdateFertTransactionFreeze,
  useUpdateODFreeze,
} from '../../../../services/freeze/hooks';
import {
  CONTAMINANT_PRESENT_VALUE,
  FIELD_NAMES,
  NO_EMBRYOS_PRODUCED_VALUE,
} from '../../utils/constants';
import { SelectionService } from '../../utils/selectionService';
import { IncompleteEmbryo } from '../../utils/types';

export function useFreeze({
  fertilizationDate,
  fertilizationSiteId,
  searchQuery,
  gridRef,
}: {
  fertilizationDate: string;
  fertilizationSiteId: string | null;
  searchQuery: string;
  gridRef: React.RefObject<AgGridReact>;
}) {
  const toast = useToast();
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();

  const [dataVersion, setDataVersion] = useState(0);
  const [isUpdatingFertilizations, setIsUpdatingFertilizations] =
    useState(false);
  const [showIncompleteEmbryosDialog, setShowIncompleteEmbryosDialog] =
    useState(false);
  const updateFormRef = useRef<{
    form: any;
    qcForm: any;
    setIsUpdateRunning: (val: boolean) => void;
  } | null>(null);
  const incompleteEmbryosRef = useRef<IncompleteEmbryo[]>([]);

  const { currentData: fertilizationFreezeData } =
    useFertilizationListFreezeQuery({
      fertilizationDate,
      fertilizationSiteId,
      searchQuery,
    });

  const {
    data: fertEmbryoData,
    refetch: _refetchFertEmbryoData,
    isLoading: embryosLoading,
  } = useFertAndEmbryosFreezeQuery({
    fertilizationDate,
    fertilizationSiteId,
    fertilizationIds: fertilizationFreezeData?.map(
      (fert) => fert.fertId as string,
    ),
  });
  // Stable refetch reference to prevent grid re-renders from memo failures
  const refetchFertEmbryoDataRef = useRef(_refetchFertEmbryoData);
  refetchFertEmbryoDataRef.current = _refetchFertEmbryoData;
  const refetchFertEmbryoData = useCallback(() => {
    refetchFertEmbryoDataRef.current();
  }, []);
  const { mutateAsync: applyApprovalNumbersToEmbryos } =
    useApplyApprovalNumbersToEmbryos();

  useEffect(() => {
    if (!fertEmbryoData || !fertilizationFreezeData) return;

    const embryosByFertId = new Map<string, (typeof fertEmbryoData)[number]>();
    for (const row of fertEmbryoData) {
      embryosByFertId.set(row.fertId, row);
    }

    const updates: UpdateApprovalNumberInput[] = [];

    for (const fert of fertilizationFreezeData) {
      const fertId = fert.fertId as string;
      const approvalNumberId = fert.approvalNumberId ?? null;

      if (!fertId || !approvalNumberId) continue;

      const embryosRow = embryosByFertId.get(fertId);
      if (!embryosRow?.liveEmbryos?.length) continue;

      const embryoIds = Array.from(
        new Set(embryosRow.liveEmbryos.map((e) => e.id).filter(Boolean)),
      ) as string[];

      if (embryoIds.length === 0) continue;

      updates.push({ fertId, approvalNumberId, embryoIds });
    }

    if (updates.length === 0) return;

    applyApprovalNumbersToEmbryos({ updates });
  }, [fertEmbryoData, fertilizationFreezeData]);

  const { mutateAsync: updateFertilization } = useUpdateFertTransactionFreeze();
  const updateFertilizationRef = useRef(updateFertilization);
  updateFertilizationRef.current = updateFertilization;

  const { mutateAsync: updateODFreezeAsync, isLoading: updateODFreezeLoading } =
    useUpdateODFreeze();
  const { invalidateFertilizationList, invalidateFertAndEmbryos } =
    useInvalidateFreezeQueries();

  const fertilizationData = useMemo(() => {
    if (fertilizationFreezeData) {
      const clonedData = cloneDeep(fertilizationFreezeData);
      setDataVersion((v) => v + 1);
      return clonedData;
    }

    return undefined;
  }, [fertilizationFreezeData]);

  const fertEmbryosData = useMemo(() => {
    if (fertEmbryoData) {
      const clonedData = cloneDeep(fertEmbryoData);
      setDataVersion((v) => v + 1);
      return clonedData;
    }
    return [];
  }, [fertEmbryoData]);

  const handleUpdateFertilization = useCallback(
    async (params: CellValueChangedEvent) => {
      // Defer selection to avoid disrupting AG Grid's tab navigation
      setTimeout(() => params.node.setSelected(true), 0);
      const field = params.column.getColDef().field as string;
      let value = params.data[field];

      if (field === FIELD_NAMES.NO_EMBRYOS_PRODUCED) {
        value = value === true ? NO_EMBRYOS_PRODUCED_VALUE : '';
      }
      if (field === FIELD_NAMES.CONTAMINANT_PRESENT) {
        value = value === true ? CONTAMINANT_PRESENT_VALUE : '';
      }

      await updateFertilizationRef.current({
        id: params.data.fertId,
        field,
        value: value?.toString(),
      });
    },
    [],
  );

  const updateFertilizationField = useCallback(
    async (fertId: string, field: string, value: string, form?: any) => {
      await updateFertilization({
        id: fertId,
        field,
        value,
      }).then(() => {
        if (form?.predictionTech) {
          updateFertilization({
            id: fertId,
            field: FIELD_NAMES.PREDICTION_TECH,
            value: form.predictionTech,
          });
        }
      });
    },
    [updateFertilization],
  );

  const processUpdate = useCallback(
    async (
      form: any,
      qcForm: any,
      setIsUpdateRunning: (val: boolean) => void,
    ) => {
      setIsUpdatingFertilizations(true);
      setIsUpdateRunning(true);

      try {
        const { tempEmbryoIds, liveEmbryoIds, fertilizationIds } =
          SelectionService.getSelection();
        const fertRecords: UpdateODFreezeInput[] = [];
        let hasErrors = false;

        const selectedFerts =
          fertilizationData?.filter((fert) =>
            fertilizationIds.includes(fert.fertId as string),
          ) || [];

        for (const fert of selectedFerts) {
          const fertWithEmbryos = fertEmbryosData?.find(
            (f) => f.fertId === fert.fertId,
          );

          if (!fertWithEmbryos) continue;

          const fertTempEmbryos = tempEmbryoIds.filter((id) =>
            fertWithEmbryos.tempEmbryos?.some(
              (emb) => emb.embryoFreezeEntryId === id,
            ),
          );

          const fertLiveEmbryos = liveEmbryoIds.filter((id) =>
            fertWithEmbryos.liveEmbryos?.some((emb) => emb.id === id),
          );

          const input = {
            fertID: fert.fertId,
            intEmbIDs: fertTempEmbryos,
            embIDs: fertLiveEmbryos.toString(),
            storageLocID:
              form.storageLocation === '' ? null : form.storageLocation,
            freezeLocationId: form.freezeLocation,
            freezeDay: form.freezeDay?.toString(),
            predictionTechID: form.predictionTech,
            graderID: form.grader,
            freezeTechID: form.freezeTech,
            freezer: form.freezer === '' ? null : form.freezer,
            embsPerVitStraw: form.embryosPerVitStraw?.toString(),
            embsPerStraw: form.embryosPerStraw?.toString(),
            eg: qcForm?.qcEg10 as string,
            egDate: qcForm?.qcEg10Date
              ? formatDate(
                  qcForm?.qcEg10Date?.toString() as string,
                  'MM-dd-yyyy',
                )
              : null,
            trypsinDate: qcForm?.qcTryPsInDate
              ? formatDate(
                  qcForm?.qcTryPsInDate?.toString() as string,
                  'MM-dd-yyyy',
                )
              : null,
            sucroseDate: qcForm?.qcSucroseDate
              ? formatDate(
                  qcForm?.qcSucroseDate?.toString() as string,
                  'MM-dd-yyyy',
                )
              : null,
            ehmDate: qcForm?.qcehmDate
              ? formatDate(
                  qcForm?.qcehmDate?.toString() as string,
                  'MM-dd-yyyy',
                )
              : null,
            freezingComment: qcForm?.qcfreezingComment,
            esDate: qcForm?.equilibrationSolutionDate
              ? formatDate(
                  qcForm?.equilibrationSolutionDate?.toString() as string,
                  'MM-dd-yyyy',
                )
              : null,
            vsDate: qcForm?.vitrificationSolutionDate
              ? formatDate(
                  qcForm?.vitrificationSolutionDate?.toString() as string,
                  'MM-dd-yyyy',
                )
              : null,
            strawLotNo: qcForm?.qcStrawLotNumber?.toString(),
            qcSterileStrawLotNumber:
              qcForm?.qcSterileStrawLotNumber?.toString(),
            vitStrawLotNo: qcForm?.qcVitStrawLotNumber?.toString(),
            qc12WellPlates: qcForm?.qc12WellPlates?.toString(),
          };

          fertRecords.push(input);
        }

        for (const fertRecord of fertRecords) {
          try {
            await updateODFreezeAsync({ input: fertRecord, dtplus: form.dt });
          } catch (error) {
            hasErrors = true;
            toast.show({
              severity: 'error',
              summary: 'Update Failed',
              detail: `Failed to update mating ${fertRecord.fertID}: ${error}`,
              life: ToastLife.LONG,
            });
          }
        }

        if (!hasErrors) {
          const newParams = new URLSearchParams(params);
          newParams.delete('temp');
          setParams(newParams);

          SelectionService.setSelection({
            liveEmbryoIds: [],
            tempEmbryoIds: [],
            fertilizationIds: [],
            lastUpdated: Date.now(),
          });

          await Promise.all([
            dispatch(
              fertilizationAppApi.util.invalidateTags([
                'EmbryosFreeze',
                'FertilizationListFreeze',
                'EmbryoListFreezeTemp',
              ]),
            ),
            invalidateFertilizationList(),
            invalidateFertAndEmbryos(),
            refetchFertEmbryoData(),
          ]);

          if (gridRef.current?.api) {
            gridRef.current.api.refreshCells({ force: true });
            gridRef.current.api.redrawRows();
          }

          toast.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Successfully updated all selected matings',
          });
        } else {
          toast.show({
            severity: 'warn',
            summary: 'Partial Update',
            detail:
              'Some matings failed to update. Please check error messages above.',
            life: ToastLife.LONG,
          });
        }

        setIsUpdatingFertilizations(false);
        setIsUpdateRunning(false);
      } catch (error) {
        console.error('Error in processUpdate:', error);
        setIsUpdatingFertilizations(false);
        setIsUpdateRunning(false);
      }
    },
    [
      fertilizationData,
      fertEmbryosData,
      updateODFreezeAsync,
      toast,
      params,
      setParams,
      dispatch,
      refetchFertEmbryoData,
      gridRef,
    ],
  );

  const handleUpdateODFreeze = useCallback(
    async (
      form: any,
      qcForm: any,
      setIsUpdateRunning: (val: boolean) => void,
    ) => {
      try {
        const { tempEmbryoIds, liveEmbryoIds, fertilizationIds } =
          SelectionService.getSelection();

        const selectedFerts =
          fertilizationData?.filter((fert) =>
            fertilizationIds.includes(fert.fertId as string),
          ) || [];

        let hasIncompleteEmbryos = false;
        incompleteEmbryosRef.current = [];

        for (const fert of selectedFerts) {
          const fertWithEmbryos = fertEmbryosData?.find(
            (f) => f.fertId === fert.fertId,
          );

          if (!fertWithEmbryos) continue;

          const fertTempEmbryos = tempEmbryoIds.filter((id) =>
            fertWithEmbryos.tempEmbryos?.some(
              (emb) => emb.embryoFreezeEntryId === id,
            ),
          );

          for (const embryo of fertWithEmbryos.tempEmbryos || []) {
            if (
              embryo.embryoFreezeEntryId &&
              fertTempEmbryos.includes(embryo.embryoFreezeEntryId)
            ) {
              const missingFields: string[] = [];
              if (!embryo.stage) missingFields.push('Stage');
              if (!embryo.grade) missingFields.push('Grade');
              if (!embryo.ownerId) missingFields.push('Owner');

              if (!form.freezeDay && !embryo.d65 && !embryo.d7) {
                missingFields.push('Freeze Day');
              }

              if (!form.grader) {
                missingFields.push('Grader');
              }

              const fertWithExport = fertilizationData?.find(
                (f) => f.fertId === embryo.fertilizationId,
              );
              if (
                fertWithExport &&
                fertWithExport.export !== 'No' &&
                !embryo.tt
              ) {
                missingFields.push('TT');
              }

              if (missingFields.length > 0) {
                hasIncompleteEmbryos = true;
                incompleteEmbryosRef.current.push({
                  plateNumber: fert.plateNumber || 0,
                  embryoName: embryo.name || 'Unknown',
                  missingFields,
                });
              }
            }
          }
        }

        if (hasIncompleteEmbryos) {
          updateFormRef.current = { form, qcForm, setIsUpdateRunning };
          setShowIncompleteEmbryosDialog(true);
        } else {
          await processUpdate(form, qcForm, setIsUpdateRunning);
        }
      } catch (e) {
        toast.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Error: ' + e,
          life: ToastLife.LONG,
        });
        setIsUpdateRunning(false);
      }
    },
    [fertilizationData, fertEmbryosData, processUpdate, toast],
  );

  const handleRefresh = useCallback(() => {
    const newParams = new URLSearchParams(params);
    newParams.delete('temp');
    setParams(newParams);
    SelectionService.updateSelection('temp', []);
    SelectionService.updateSelection('live', []);
    SelectionService.updateSelection('fert', []);
    invalidateFertilizationList();
    invalidateFertAndEmbryos();
  }, [
    dispatch,
    params,
    setParams,
    invalidateFertilizationList,
    invalidateFertAndEmbryos,
  ]);

  const handleIncompleteEmbryosAccept = useCallback(async () => {
    if (updateFormRef.current) {
      const { form, qcForm, setIsUpdateRunning } = updateFormRef.current;
      await processUpdate(form, qcForm, setIsUpdateRunning);
      updateFormRef.current = null;
    }
    setShowIncompleteEmbryosDialog(false);
  }, [processUpdate]);

  const handleIncompleteEmbryosReject = useCallback(() => {
    if (updateFormRef.current) {
      updateFormRef.current.setIsUpdateRunning(false);
      updateFormRef.current = null;
    }
    setShowIncompleteEmbryosDialog(false);
    invalidateFertilizationList();
    invalidateFertAndEmbryos();
  }, [dispatch, invalidateFertilizationList, invalidateFertAndEmbryos]);

  return {
    fertilizationData,
    fertEmbryosData,
    embryosLoading,
    updateODFreezeLoading,
    isUpdatingFertilizations,
    showIncompleteEmbryosDialog,
    setShowIncompleteEmbryosDialog,
    handleIncompleteEmbryosAccept,
    handleIncompleteEmbryosReject,
    handleRefresh,
    handleUpdateODFreeze,
    handleUpdateFertilization,
    updateFertilizationField,
    refetchFertEmbryoData,
    dataVersion,
    incompleteEmbryosRef,
  };
}
